import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTeenOverview } from './use-teen-overview';
import { PageContainer } from '@/components/PageContainer';
import { ArrowRightIcon, ShieldCheckIcon } from '@/components/ui/Icons';

/**
 * The frame every teen page sits in.
 *
 * Same idea as the parent console: one rail, carrying the counts, so a teen
 * never has to go looking for the thing that is waiting on them. The guardian
 * banner sits above it because a blocked teen needs to know why before they
 * read anything else. [FR-M01.2, Teen step 20]
 */

interface Tab {
  label: string;
  to: string;
  badge?: number;
}

export function TeenShell({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  const overview = useTeenOverview();
  const data = overview.data;

  const tabs: Tab[] = [
    { label: 'Overview', to: '/teen' },
    { label: 'My work', to: '/my-work', badge: data?.jobOffers ?? 0 },
    { label: 'Saved', to: '/saved', badge: 0 },
    { label: 'Money', to: '/money' },
  ];

  const blocked = data ? data.isMinor && !data.guardianApproved : false;

  return (
    <PageContainer>
      {blocked && (
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-sun-200 bg-sun-50 px-5 py-4">
          <ShieldCheckIcon className="size-5 shrink-0 text-sun-700" />
          <p className="flex-1 text-sm font-medium text-sun-700">
            You are waiting on a guardian to approve your account. You can finish your profile and
            save jobs meanwhile — you just cannot apply yet.
          </p>
          <Link
            to="/pending"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-sun-700 hover:underline"
          >
            Details
            <ArrowRightIcon className="size-4" />
          </Link>
        </div>
      )}

      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold tracking-[0.14em] text-brand-600 uppercase">
            My TeenTack
          </p>
          <h1 className="mt-2 text-2xl font-extrabold text-ink sm:text-3xl">{title}</h1>
          {description && (
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-500">{description}</p>
          )}
        </div>
        {action ?? (
          <Link
            to="/jobs"
            className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-700"
          >
            Find jobs
            <ArrowRightIcon className="size-4" />
          </Link>
        )}
      </header>

      <nav
        aria-label="My TeenTack"
        className="mt-6 -mx-1 flex gap-1 overflow-x-auto border-b border-line pb-px"
      >
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end
            className={({ isActive }) =>
              `relative flex shrink-0 items-center gap-2 rounded-t-lg px-4 py-3 text-sm font-semibold whitespace-nowrap transition ${
                isActive
                  ? 'text-brand-700 after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full after:bg-brand-600'
                  : 'text-slate-500 hover:text-ink'
              }`
            }
          >
            {tab.label}
            {tab.badge !== undefined && tab.badge > 0 && (
              <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[0.65rem] font-bold text-white">
                {tab.badge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="mt-8">{children}</div>
    </PageContainer>
  );
}
